// NOTE: First-pass UK PECR + UK GDPR cookie notice for the TS1 dashboard. Not yet
// reviewed by a solicitor. If non-essential cookies (analytics, marketing pixels,
// session-recording, etc.) are added later, a separate consent banner will be
// required to satisfy PECR.

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const lastUpdated = '11 May 2026';

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    body: (
      <p>
        This Cookie Policy explains how Thermal Systems Ltd (&quot;we&quot;, &quot;our&quot;, or
        &quot;us&quot;) uses cookies and similar storage technologies in the TS1 monitoring dashboard,
        mobile applications, and related services (the &quot;Service&quot;). It should be read alongside our{' '}
        <Link to="/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Privacy Policy
        </Link>
        .
      </p>
    ),
  },
  {
    id: 'what',
    title: '2. What are cookies?',
    body: (
      <p>
        Cookies are small text files placed on your device when you visit a website. Similar technologies
        include browser local storage, session storage, and IndexedDB, which work in a comparable way. In this
        policy we use &quot;cookies&quot; as shorthand for all of these.
      </p>
    ),
  },
  {
    id: 'categories',
    title: '3. Categories of cookies we use',
    body: (
      <>
        <p>
          The Service is a business tool and we keep cookie use to a minimum. We currently only set cookies
          and storage items that are strictly necessary for the Service to function, or that store your own
          preferences.
        </p>
        <p className="mt-3 font-semibold">Strictly necessary</p>
        <ul className="list-disc pl-6 space-y-2 mt-2">
          <li>
            <strong>Authentication session cookie</strong> (<code className="text-xs">authToken</code>):
            httpOnly, secure, SameSite=None. Set after successful sign-in to keep you logged in. Required for
            the Service to function. Cleared on sign-out or token expiry.
          </li>
          <li>
            <strong>Session integrity tokens:</strong> short-lived tokens used during sign-in, password
            reset, and biometric registration flows.
          </li>
        </ul>
        <p className="mt-4 font-semibold">Functionality and preferences</p>
        <ul className="list-disc pl-6 space-y-2 mt-2">
          <li>
            <strong>Theme preference</strong> (light/dark): stored locally on your device.
          </li>
          <li>
            <strong>Last-used company name</strong>: stored locally on your device to pre-fill the login
            form on your next visit. You can clear this by signing out and clearing your browser&apos;s site
            data.
          </li>
          <li>
            <strong>Biometric / WebAuthn credentials</strong>: where you opt in to biometric sign-in,
            credential identifiers may be stored on your device by your operating system or browser to enable
            passkey-style authentication. The biometric data itself never leaves your device.
          </li>
        </ul>
        <p className="mt-4 font-semibold">Analytics, advertising, or tracking</p>
        <p className="mt-2">
          We do <strong>not</strong> currently use any third-party analytics, advertising, marketing, or
          cross-site tracking cookies in the Service. If we add any in the future, we will update this policy
          and present a consent mechanism in accordance with the UK Privacy and Electronic Communications
          Regulations (PECR) and the UK GDPR.
        </p>
      </>
    ),
  },
  {
    id: 'legal-basis',
    title: '4. Legal basis',
    body: (
      <p>
        Under the UK Privacy and Electronic Communications Regulations (PECR), strictly necessary cookies do
        not require your consent. We rely on this exemption for the authentication cookies described above.
        Functionality and preference items are stored locally on your device based on actions you choose to
        take in the Service (for example, switching theme or enrolling for biometric sign-in).
      </p>
    ),
  },
  {
    id: 'managing',
    title: '5. Managing cookies',
    body: (
      <>
        <p>
          You can control cookies through your browser settings, including blocking or deleting them. Note
          that if you block strictly necessary cookies you will not be able to sign in to the Service.
        </p>
        <p className="mt-3">
          You can also clear locally stored preferences and biometric credentials from within your browser or
          device settings. To remove a registered biometric credential from your account, sign in and use the
          options in your user profile or security settings.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: '6. Changes to this policy',
    body: (
      <p>
        We may update this Cookie Policy from time to time. The updated version will be posted on this page
        with a new &quot;Last updated&quot; date. Material changes, in particular the introduction of any
        non-essential cookies, will be accompanied by a consent prompt where required by law.
      </p>
    ),
  },
  {
    id: 'contact',
    title: '7. Contact us',
    body: (
      <p>
        Questions about this Cookie Policy can be sent to Thermal Systems Ltd via our website{' '}
        <a
          href="https://thermal-systems.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          thermal-systems.co.uk
        </a>
        , by telephone on{' '}
        <a href="tel:02034881030" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          020 3488 1030
        </a>
        , or through the contact channels your organisation has been given for support.
      </p>
    ),
  },
];

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to sign in
        </Link>

        <header className="mb-10 pb-8 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">Cookie Policy</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            How Thermal Systems Ltd uses cookies and similar technologies in the TS1 Dashboard.
          </p>
        </header>

        <nav className="mb-10 p-4 rounded-lg bg-app-panel dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Contents</h2>
          <ul className="text-sm space-y-1 text-indigo-600 dark:text-indigo-400">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="hover:underline">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-10 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
          {sections.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{s.title}</h2>
              {s.body}
            </section>
          ))}
        </div>

        <footer className="mt-14 pt-8 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
            <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Terms of Service
            </Link>
            <Link to="/compliance" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Security &amp; Compliance
            </Link>
            <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/dpa" className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">
              Data Processing Agreement
            </Link>
          </div>
          <p>© {new Date().getFullYear()} Thermal Systems Ltd. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
